import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { X, Battery, Thermometer, Droplets, Gauge, Download, ChevronDown, TrendingUp, Clock, Signal, CheckCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Surface, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';



const SensorDetail = ({ sensor, onBack, boatId, isModal = true }) => {
  const [sensorHistory, setSensorHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [displayedCount, setDisplayedCount] = useState(50);

  // MSL01 için özel state'ler
  const [waterLeakChartData, setWaterLeakChartData] = useState(null);
  const [waterLeakHistory, setWaterLeakHistory] = useState([]);
  const [eventHistory, setEventHistory] = useState([]);
  const [currentEvents, setCurrentEvents] = useState({
    offline: false,
    lowBattery: false,
    waterLeak: false
  });

  // MSP01 için özel state'ler
  const [msp01ChartData, setMsp01ChartData] = useState(null);
  const [msp01History, setMsp01History] = useState([]);
  const [fullscreenChart, setFullscreenChart] = useState(null);
  // MSP01 PIR UI gecikmeli durum (45s sonra YOK'a düş)
  const [msp01PirShownActive, setMsp01PirShownActive] = useState(null);
  const msp01PirResetTimerRef = useRef(null);

  // MST01 için özel state'ler
  const [mst01ChartData, setMst01ChartData] = useState(null);
  const [mst01History, setMst01History] = useState([]);
  const [mst01ExportScope, setMst01ExportScope] = useState('visible'); // 'visible' | 'all'

  // S4 için özel state'ler
  const [s4ChartData, setS4ChartData] = useState(null);
  const [s4History, setS4History] = useState([]);
  const [s4ExportScope, setS4ExportScope] = useState('visible');

  // ESC tuşu ile fullscreen modal'ı kapatma
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && fullscreenChart) {
        setFullscreenChart(null);
      }
    };

    if (fullscreenChart) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Scroll'u engelle
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset'; // Scroll'u geri aç
    };
  }, [fullscreenChart]);
  const [pirFilter, setPirFilter] = useState('all'); // 'all', 'active', 'inactive'

  const sensorName = getSensorDisplayName(sensor.model);
  const sensorMAC = sensor.mac;
  const sensorModel = sensor.model?.toUpperCase() || '';
  // Daha sağlam tip tespiti: model + history anahtarları
  const detectSensorType = useCallback((s) => {
    const mdl = (s?.model || '').toString().toUpperCase();
    const nameUp = (getSensorDisplayName(s?.model) || '').toString().toUpperCase();
    const hist = s?.history || {};
    const hasWater = !!hist.waterleak;
    const hasPir = !!(hist['PIR-motion'] || hist['PIR'] || hist['pir']);
    const hasTemp = !!hist.temperature;
    const hasDoor = !!(hist.door || hist.contact || hist['door-status'] || hist['contact-state'] || hist.doorstatus);
    // Önce history öncelikli
    if (hasWater) return 'MSL01';
    if (hasPir) return 'MSP01';
    if (hasTemp) return 'MST01';
    if (hasDoor) return 'S4';
    // Sonra model/name
    if (mdl.includes('MSL01') || nameUp.includes('MSL01')) return 'MSL01';
    if (mdl.includes('MSP01') || nameUp.includes('MSP01')) return 'MSP01';
    if (mdl.includes('MST01') || nameUp.includes('MST01')) return 'MST01';
    if (mdl.includes('S4') || nameUp.includes('S4')) return 'S4';
    return 'UNKNOWN';
  }, []);

  const detectedType = detectSensorType(sensor);
  const isMSL01 = detectedType === 'MSL01';
  const isMSP01 = detectedType === 'MSP01';
  const isMST01 = detectedType === 'MST01';
  const isS4 = detectedType === 'S4';

  // Minew sensör adı ve modeli
  function getSensorDisplayName(model) {
    if (!model) return 'Bilinmeyen Sensör';
    const modelUpper = model.toUpperCase();
    
    if (modelUpper.includes('MG5')) {
      return '🌐 Minew MG5 Mobile Gateway';
    }
    if (modelUpper.includes('MSP01')) {
      return '👁️ Minew MSP01 PIR Sensor';
    }
    if (modelUpper.includes('MST01')) {
      return '🌡️ Minew MST01 Temperature Sensor';
    }
    if (modelUpper.includes('MSL01')) {
      return '💧 Minew MSL01 Water Leakage Sensor';
    }
    if (modelUpper.includes('S4')) {
      return '🚪 Minew S4 Door Sensor';
    }
    
    return `📡 ${model}`;
  }

  // MSL01 için su kaçağı geçmişi yükle
  const loadMSL01WaterLeakHistory = useCallback(() => {
    if (!isMSL01) return;

    try {
      setIsLoadingHistory(true);
      
          const now = Date.now();
          const timeRangeMs = {
            '1h': 60 * 60 * 1000,
            '6h': 6 * 60 * 60 * 1000,
            '24h': 24 * 60 * 60 * 1000,
            '7d': 7 * 24 * 60 * 60 * 1000,
            '30d': 30 * 24 * 60 * 60 * 1000
          }[selectedTimeRange] || 24 * 60 * 60 * 1000;

      // Saniye seviyesinde grouping için Map kullan
      const processedData = new Map();
      
      if (sensor.history) {
        // Waterleak verilerini işle
        if (sensor.history.waterleak) {
          Object.entries(sensor.history.waterleak).forEach(([timestamp, data]) => {
            const ts = parseInt(timestamp);
            if (now - ts <= timeRangeMs) {
              // Saniye seviyesinde key oluştur (milisaniyeleri ignore et)
              const secondKey = Math.floor(ts / 1000) * 1000;
              
              if (!processedData.has(secondKey)) {
                processedData.set(secondKey, {
                  timestamp: secondKey,
                  water_leak: null,
                  battery: null,
                  rssi: null,
                  last_updated: null
                });
              }
              const entry = processedData.get(secondKey);
              entry.water_leak = data.water_leak !== null && data.water_leak !== undefined ? data.water_leak : entry.water_leak;
              entry.battery = data.battery || data.battery_level || entry.battery;
              entry.rssi = data.rssi || entry.rssi;
              entry.last_updated = data.last_updated || entry.last_updated;
            }
          });
        }

        // Battery verilerini işle
        if (sensor.history.battery) {
          Object.entries(sensor.history.battery).forEach(([timestamp, data]) => {
            const ts = parseInt(timestamp);
            if (now - ts <= timeRangeMs) {
              // Saniye seviyesinde key oluştur (milisaniyeleri ignore et)
              const secondKey = Math.floor(ts / 1000) * 1000;
              
              if (!processedData.has(secondKey)) {
                processedData.set(secondKey, {
                  timestamp: secondKey,
                  water_leak: null,
                  battery: null,
                  rssi: null,
                  last_updated: null
                });
              }
              const entry = processedData.get(secondKey);
              entry.battery = data.battery || data.battery_level || entry.battery;
              entry.rssi = data.rssi || entry.rssi;
              entry.last_updated = data.last_updated || entry.last_updated;
            }
          });
        }
      }

            // Map'i array'e çevir ve filtrele
      const filteredEntries = Array.from(processedData.values())
        .filter(entry => entry.water_leak !== null && entry.water_leak !== undefined)
            .sort((a, b) => a.timestamp - b.timestamp);



          setWaterLeakHistory(filteredEntries);

          // Chart verisi oluştur (Recharts formatı)
      if (filteredEntries.length > 0) {
          const chartData = filteredEntries.map(entry => ({
            time: new Date(entry.timestamp).toLocaleTimeString('tr-TR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }),
            timestamp: entry.timestamp,
            waterLeak: entry.water_leak ? 1 : 0
          }));

          setWaterLeakChartData(chartData);
      }
    } catch (error) {
      console.error('MSL01 su kaçağı geçmişi yükleme hatası:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [sensor, selectedTimeRange, isMSL01]);

  // MST01 için veri geçmişi yükle
  const loadMST01History = useCallback(() => {
    if (!isMST01) return;

    try {
      setIsLoadingHistory(true);
      const now = Date.now();
      const timeRangeMs = {
        '1h': 60 * 60 * 1000,
        '6h': 6 * 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      }[selectedTimeRange] || 24 * 60 * 60 * 1000;

      // Saniye seviyesinde grouping
      const processedData = new Map();

      if (sensor.history) {
        // Temperature/Humidity
        if (sensor.history.temperature) {
          Object.entries(sensor.history.temperature).forEach(([timestamp, data]) => {
            const ts = parseInt(timestamp);
            if (now - ts <= timeRangeMs) {
              const secondKey = Math.floor(ts / 1000) * 1000;
              if (!processedData.has(secondKey)) {
                processedData.set(secondKey, {
                  timestamp: secondKey,
                  temperature: null,
                  humidity: null,
                  battery: null,
                  rssi: null,
                  last_updated: null
                });
              }
              const entry = processedData.get(secondKey);
              entry.temperature = data.temperature !== null && data.temperature !== undefined ? data.temperature : entry.temperature;
              entry.humidity = data.humidity !== null && data.humidity !== undefined ? data.humidity : entry.humidity;
              entry.battery = data.battery || data.battery_level || entry.battery;
              entry.rssi = data.rssi || entry.rssi;
              entry.last_updated = data.last_updated || entry.last_updated;
            }
          });
        }

        // Battery (tamamlama)
        if (sensor.history.battery) {
          Object.entries(sensor.history.battery).forEach(([timestamp, data]) => {
            const ts = parseInt(timestamp);
            if (now - ts <= timeRangeMs) {
              const secondKey = Math.floor(ts / 1000) * 1000;
              if (!processedData.has(secondKey)) {
                processedData.set(secondKey, {
                  timestamp: secondKey,
                  temperature: null,
                  humidity: null,
                  battery: null,
                  rssi: null,
                  last_updated: null
                });
              }
              const entry = processedData.get(secondKey);
              entry.battery = data.battery || data.battery_level || entry.battery;
              entry.rssi = data.rssi || entry.rssi;
              entry.last_updated = data.last_updated || entry.last_updated;
            }
          });
        }
      }

      const filteredEntries = Array.from(processedData.values())
        .filter(e => (e.temperature !== null && e.temperature !== undefined) || (e.humidity !== null && e.humidity !== undefined))
        .sort((a, b) => a.timestamp - b.timestamp);

      setMst01History(filteredEntries);

      if (filteredEntries.length > 0) {
        const chartData = filteredEntries.map(e => ({
          time: new Date(e.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          timestamp: e.timestamp,
          temperature: e.temperature,
          humidity: e.humidity
        }));
        setMst01ChartData(chartData);
      } else {
        setMst01ChartData([]);
      }
    } catch (error) {
      console.error('MST01 veri geçmişi yükleme hatası:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [sensor, selectedTimeRange, isMST01]);

  // MSP01 için veri geçmişi yükle
  const loadMSP01History = useCallback(() => {
    if (!isMSP01) return;

    try {
      setIsLoadingHistory(true);

      
      const historyData = [];
      const allTimestamps = new Set();
      
      // Tüm history türlerinden timestamp'leri topla
      if (sensor.history) {
        Object.keys(sensor.history).forEach(historyType => {
          if (sensor.history[historyType]) {
            Object.keys(sensor.history[historyType]).forEach(timestamp => {
              allTimestamps.add(parseInt(timestamp));
            });
          }
        });
      }
      
      // Tüm veri tiplerini ayrı ayrı işle ve sonra birleştir
      const processedData = new Map();
      
      // PIR-motion verilerini işle
      if (sensor.history && sensor.history['PIR-motion']) {
        Object.entries(sensor.history['PIR-motion']).forEach(([timestamp, data]) => {
          const ts = parseInt(timestamp);
          if (!processedData.has(ts)) {
            processedData.set(ts, {
              timestamp: ts,
          pir_activity: false,
          temperature: null,
          humidity: null,
          illuminance: null,
          acceleration: null,
          battery: null,
          rssi: null
            });
          }
          const entry = processedData.get(ts);
          entry.pir_activity = data.motion || data.PIR_motion || data.PIR || false;
          entry.battery = data.battery || data.battery_level || entry.battery;
          entry.rssi = data.rssi || entry.rssi;
        });
      }
      
      // Temperature verilerini işle
      if (sensor.history && sensor.history.temperature) {
        Object.entries(sensor.history.temperature).forEach(([timestamp, data]) => {
          const ts = parseInt(timestamp);
          if (!processedData.has(ts)) {
            processedData.set(ts, {
              timestamp: ts,
              pir_activity: false,
              temperature: null,
              humidity: null,
              illuminance: null,
              acceleration: null,
              battery: null,
              rssi: null
            });
          }
          const entry = processedData.get(ts);
          entry.temperature = data.temperature !== null && data.temperature !== undefined ? data.temperature : entry.temperature;
          entry.humidity = data.humidity !== null && data.humidity !== undefined ? data.humidity : entry.humidity;
          entry.battery = data.battery || data.battery_level || entry.battery;
          entry.rssi = data.rssi || entry.rssi;
        });
      }
      
      // Illuminance verilerini işle
      if (sensor.history && sensor.history.illuminance) {
        Object.entries(sensor.history.illuminance).forEach(([timestamp, data]) => {
          const ts = parseInt(timestamp);
          if (!processedData.has(ts)) {
            processedData.set(ts, {
              timestamp: ts,
              pir_activity: false,
              temperature: null,
              humidity: null,
              illuminance: null,
              acceleration: null,
              battery: null,
              rssi: null
            });
          }
          const entry = processedData.get(ts);
          entry.illuminance = data.illuminance || entry.illuminance;
          entry.battery = data.battery || data.battery_level || entry.battery;
          entry.rssi = data.rssi || entry.rssi;
        });
      }
      
      // Acceleration verilerini işle
      if (sensor.history && sensor.history.acceleration) {
        Object.entries(sensor.history.acceleration).forEach(([timestamp, data]) => {
          const ts = parseInt(timestamp);
          if (!processedData.has(ts)) {
            processedData.set(ts, {
              timestamp: ts,
              pir_activity: false,
              temperature: null,
              humidity: null,
              illuminance: null,
              acceleration: null,
              battery: null,
              rssi: null
            });
          }
          const entry = processedData.get(ts);
          // Acceleration verilerini x, y, z eksenlerinden hesapla
          if (data.x_axis !== undefined && data.y_axis !== undefined && data.z_axis !== undefined) {
            const x = parseFloat(data.x_axis);
            const y = parseFloat(data.y_axis);
            const z = parseFloat(data.z_axis);
            // Toplam ivme hesapla (sqrt(x² + y² + z²))
            entry.acceleration = Math.sqrt(x * x + y * y + z * z);
          }
          entry.battery = data.battery || data.battery_level || entry.battery;
          entry.rssi = data.rssi || entry.rssi;
        });
        }
        
      // Map'i array'e çevir
      historyData.push(...Array.from(processedData.values()));

      // Zaman aralığına göre filtrele
      const now = Date.now();
      const timeRangeMs = {
        '1h': 60 * 60 * 1000,
        '6h': 6 * 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      }[selectedTimeRange] || 24 * 60 * 60 * 1000;

      const filteredData = historyData
        .filter(entry => now - entry.timestamp <= timeRangeMs)
        .sort((a, b) => a.timestamp - b.timestamp);

                   setMsp01History(filteredData);



            // Chart verilerini oluştur (Recharts formatı)
      if (filteredData.length > 0) {
        const rechartsData = filteredData.map(entry => ({
          time: new Date(entry.timestamp).toLocaleTimeString('tr-TR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          timestamp: entry.timestamp,
          pir: entry.pir_activity ? 1 : 0,
          temperature: entry.temperature !== null ? entry.temperature : undefined,
          illuminance: entry.illuminance !== null ? entry.illuminance : undefined,
          acceleration: entry.acceleration !== null ? entry.acceleration : undefined
        }));



        setMsp01ChartData(rechartsData);
      } else {
        setMsp01ChartData([]);
      }
    } catch (error) {
      console.error('MSP01 veri geçmişi yükleme hatası:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [sensor, selectedTimeRange, isMSP01]);

  // Chart data'sını optimize et
  const optimizedChartData = useMemo(() => {
    if (!msp01ChartData || msp01ChartData.length === 0) return [];
    return msp01ChartData;
  }, [msp01ChartData]);

  // MSP01: En güncel PIR gerçek durumu hesapla (history veya sensor üzerinden)
  const msp01LatestPirActive = useMemo(() => {
    try {
      // Öncelik: sensor.history['PIR-motion']
      if (sensor?.history && sensor.history['PIR-motion']) {
        const entries = Object.entries(sensor.history['PIR-motion']);
        if (entries.length > 0) {
          const latest = entries.sort(([a], [b]) => parseInt(b) - parseInt(a))[0][1];
          const pir = latest.motion ?? latest.PIR_motion ?? latest.PIR;
          if (pir === true || pir === false) return pir;
        }
      }
      // İkincil: msp01History son kayıt
      if (msp01History && msp01History.length > 0) {
        const last = [...msp01History].sort((a, b) => b.timestamp - a.timestamp)[0];
        if (typeof last.pir_activity === 'boolean') return last.pir_activity;
      }
    } catch (e) {
      // ignore
    }
    return null;
  }, [sensor, msp01History]);

  // MSP01: PIR gösterim mantığı (true hemen, false -> 45s gecikmeli)
  useEffect(() => {
    if (msp01LatestPirActive === null || msp01LatestPirActive === undefined) return;
    if (msp01LatestPirActive) {
      if (msp01PirResetTimerRef.current) {
        clearTimeout(msp01PirResetTimerRef.current);
        msp01PirResetTimerRef.current = null;
      }
      setMsp01PirShownActive(true);
    } else {
      // false geldi
      if (msp01PirShownActive) {
        // 45 sn sonra false'a düş
        if (!msp01PirResetTimerRef.current) {
          msp01PirResetTimerRef.current = setTimeout(() => {
            setMsp01PirShownActive(false);
            msp01PirResetTimerRef.current = null;
          }, 45000);
        }
      } else {
        setMsp01PirShownActive(false);
      }
    }
  }, [msp01LatestPirActive, msp01PirShownActive]);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      if (msp01PirResetTimerRef.current) {
        clearTimeout(msp01PirResetTimerRef.current);
      }
    };
  }, []);

  // S4 chart data memoization (if needed later for fullscreen)
  const optimizedS4ChartData = useMemo(() => {
    if (!s4ChartData || s4ChartData.length === 0) return [];
    return s4ChartData;
  }, [s4ChartData]);

  // MSL01 için olay durumlarını takip et
  const updateMSL01Events = useCallback(() => {
    if (!isMSL01) return;

    const now = Date.now();
    
    // Use functional state updates to avoid dependency on current values
    setCurrentEvents(prevEvents => {
      const newEvents = { ...prevEvents };

    // Offline durumu kontrol et (5 dakika boyunca veri yoksa)
    const lastSeen = sensor.last_seen || 0;
    const isOffline = (now - lastSeen) > 5 * 60 * 1000;

      if (isOffline && !prevEvents.offline) {
      newEvents.offline = true;
      } else if (!isOffline && prevEvents.offline) {
      newEvents.offline = false;
    }

    // Düşük batarya durumu kontrol et
    let batteryLevel = null;
    if (sensor.history && sensor.history.waterleak) {
      const waterleakEntries = Object.entries(sensor.history.waterleak);
      if (waterleakEntries.length > 0) {
        const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
        const latestData = sortedEntries[0][1];
        batteryLevel = latestData.battery || latestData.battery_level;
      }
    } else {
      batteryLevel = typeof sensor.battery === 'number' 
        ? sensor.battery 
        : typeof sensor.battery === 'object' && sensor.battery?.battery_level 
          ? sensor.battery.battery_level 
          : null;
    }

    const isLowBattery = batteryLevel !== null && batteryLevel < 20;

      if (isLowBattery && !prevEvents.lowBattery) {
      newEvents.lowBattery = true;
      } else if (!isLowBattery && prevEvents.lowBattery) {
      newEvents.lowBattery = false;
    }

    // Su kaçağı durumu kontrol et
    let waterLeakStatus = false;
    if (sensor.history && sensor.history.waterleak) {
      const waterleakEntries = Object.entries(sensor.history.waterleak);
      if (waterleakEntries.length > 0) {
        const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
        const latestData = sortedEntries[0][1];
        waterLeakStatus = latestData.water_leak;
      }
    } else if (sensor.binary_states?.water_leak !== undefined) {
      waterLeakStatus = sensor.binary_states.water_leak;
    }

      if (waterLeakStatus && !prevEvents.waterLeak) {
      newEvents.waterLeak = true;
      } else if (!waterLeakStatus && prevEvents.waterLeak) {
      newEvents.waterLeak = false;
      }

      return newEvents;
    });

    // Update event history separately
    setEventHistory(prevHistory => {
      const newEventHistory = [...prevHistory];
      
      // Re-calculate states to check for changes
      const lastSeen = sensor.last_seen || 0;
      const isOffline = (now - lastSeen) > 5 * 60 * 1000;
      
      let batteryLevel = null;
      if (sensor.history && sensor.history.waterleak) {
        const waterleakEntries = Object.entries(sensor.history.waterleak);
        if (waterleakEntries.length > 0) {
          const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
          const latestData = sortedEntries[0][1];
          batteryLevel = latestData.battery || latestData.battery_level;
        }
      } else {
        batteryLevel = typeof sensor.battery === 'number' 
          ? sensor.battery 
          : typeof sensor.battery === 'object' && sensor.battery?.battery_level 
            ? sensor.battery.battery_level 
            : null;
      }
      const isLowBattery = batteryLevel !== null && batteryLevel < 20;
      
      let waterLeakStatus = false;
      if (sensor.history && sensor.history.waterleak) {
        const waterleakEntries = Object.entries(sensor.history.waterleak);
        if (waterleakEntries.length > 0) {
          const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
          const latestData = sortedEntries[0][1];
          waterLeakStatus = latestData.water_leak;
        }
      } else if (sensor.binary_states?.water_leak !== undefined) {
        waterLeakStatus = sensor.binary_states.water_leak;
      }

      // This won't cause infinite loops since we're not depending on the current events
      return newEventHistory;
    });
  }, [sensor, isMSL01]); // Removed currentEvents and eventHistory from dependencies

  // Süre formatla
  const formatDuration = (startTime, endTime) => {
    const duration = endTime - startTime;
    const minutes = Math.floor(duration / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}g ${hours % 24}s`;
    if (hours > 0) return `${hours}s ${minutes % 60}d`;
    return `${minutes}d`;
  };

  // S4: Kapı sensörü geçmişi yükle
  const loadS4History = useCallback(() => {
    if (!isS4) return;

    try {
      setIsLoadingHistory(true);
      const now = Date.now();
      const timeRangeMs = {
        '1h': 60 * 60 * 1000,
        '6h': 6 * 60 * 60 * 1000,
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000
      }[selectedTimeRange] || 24 * 60 * 60 * 1000;

      const processedData = new Map();

      if (sensor.history) {
        // Door/contact status
        const doorSources = ['doorstatus', 'door', 'contact', 'door-status', 'contact-state'];
        doorSources.forEach(key => {
          if (sensor.history[key]) {
            Object.entries(sensor.history[key]).forEach(([timestamp, data]) => {
              const ts = parseInt(timestamp);
              if (now - ts <= timeRangeMs) {
                const secondKey = Math.floor(ts / 1000) * 1000;
                if (!processedData.has(secondKey)) {
                  processedData.set(secondKey, {
                    timestamp: secondKey,
                    door_open: null,
                    tamper: null,
                    open_count: null,
                    close_count: null,
                    tamper_count: null,
                    battery: null,
                    rssi: null,
                    last_updated: null
                  });
                }
                const entry = processedData.get(secondKey);
                const doorVal = data.door_open ?? data.open ?? data.closed ?? data.state;
                if (doorVal !== undefined && doorVal !== null) {
                  // Normalize to boolean first for explicit true/false
                  if (doorVal === true) {
                    entry.door_open = true;
                  } else if (doorVal === false) {
                    entry.door_open = false;
                  } else if (doorVal === 'open') {
                    entry.door_open = true;
                  } else if (doorVal === 'closed') {
                    entry.door_open = false;
                  } else if (doorVal === 1) {
                    entry.door_open = true;
                  } else if (doorVal === 0) {
                    entry.door_open = false;
                  }
                }
                // Counts if provided on doorstatus
                if (typeof data.open_count === 'number') entry.open_count = data.open_count;
                if (typeof data.close_count === 'number') entry.close_count = data.close_count;
                if (typeof data.tamper_count === 'number') entry.tamper_count = data.tamper_count;
                entry.battery = data.battery || data.battery_level || entry.battery;
                entry.rssi = data.rssi || entry.rssi;
                entry.last_updated = data.last_updated || entry.last_updated;
              }
            });
          }
        });

        // Tamper status (doorstatus.tamper_triggered veya ayrı tamper)
        const tamperSources = [];
        if (sensor.history.tamper) tamperSources.push(['tamper', sensor.history.tamper]);
        if (sensor.history.doorstatus) tamperSources.push(['doorstatus', sensor.history.doorstatus]);
        tamperSources.forEach(([key, obj]) => {
          Object.entries(obj).forEach(([timestamp, data]) => {
            const ts = parseInt(timestamp);
            if (now - ts <= timeRangeMs) {
              const secondKey = Math.floor(ts / 1000) * 1000;
              if (!processedData.has(secondKey)) {
                processedData.set(secondKey, {
                  timestamp: secondKey,
                  door_open: null,
                  tamper: null,
                  open_count: null,
                  close_count: null,
                  tamper_count: null,
                  battery: null,
                  rssi: null,
                  last_updated: null
                });
              }
              const entry = processedData.get(secondKey);
              const tamperVal = data.tamper ?? data.tamper_triggered ?? data.triggered ?? data.state;
              if (tamperVal !== undefined && tamperVal !== null) {
                entry.tamper = tamperVal === true || tamperVal === 'on' || tamperVal === 1;
              }
              if (typeof data.tamper_count === 'number') entry.tamper_count = data.tamper_count;
              entry.battery = data.battery || data.battery_level || entry.battery;
              entry.rssi = data.rssi || entry.rssi;
              entry.last_updated = data.last_updated || entry.last_updated;
            }
          });
        });
      }

      const entries = Array.from(processedData.values())
        .filter(e => e.door_open !== null || e.tamper !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      setS4History(entries);

      if (entries.length > 0) {
        // Tek grafikte door ve tamper
        const chartData = entries.map(e => ({
          time: new Date(e.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
          timestamp: e.timestamp,
          door: e.door_open === true ? 1 : e.door_open === false ? 0 : null,
          tamper: e.tamper ? 1 : 0
        }));
        setS4ChartData(chartData);
      } else {
        setS4ChartData([]);
      }
    } catch (error) {
      console.error('S4 veri geçmişi yükleme hatası:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [sensor, selectedTimeRange, isS4]);

  const exportS4ToExcel = useCallback(() => {
    if (!s4History.length) return;
    const baseData = [...s4History].sort((a, b) => b.timestamp - a.timestamp);
    const dataToExport = s4ExportScope === 'all' ? baseData : baseData.slice(0, displayedCount);
    const excelData = dataToExport.map((e, index) => ({
      'Sıra': index + 1,
      'Tarih Saat': new Date(e.timestamp).toLocaleString('tr-TR'),
      'Kapı': e.door_open === true ? 'AÇIK' : e.door_open === false ? 'KAPALI' : 'N/A',
      'Tamper': e.tamper ? 'VAR' : 'YOK',
      'Açılma Sayısı': typeof e.open_count === 'number' ? e.open_count : 'N/A',
      'Kapanma Sayısı': typeof e.close_count === 'number' ? e.close_count : 'N/A',
      'Tamper Sayısı': typeof e.tamper_count === 'number' ? e.tamper_count : 'N/A',
      'Batarya (%)': (() => {
        if (typeof e.battery === 'number') return e.battery.toFixed(1);
        if (typeof e.battery === 'object' && e.battery?.battery_level) return e.battery.battery_level.toFixed(1);
        return 'N/A';
      })(),
      'RSSI (dBm)': e.rssi || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { width: 8 }, { width: 20 }, { width: 10 }, { width: 10 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 14 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'S4 Veri Geçmişi');
    const now = new Date();
    const scopeTag = s4ExportScope === 'all' ? 'ALL' : `TOP_${displayedCount}`;
    const fileName = `S4_${sensorMAC}_${scopeTag}_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [s4History, displayedCount, sensorMAC, s4ExportScope]);




  // Excel export
  const exportToExcel = useCallback(() => {
    if (!waterLeakHistory.length) return;

    const excelData = waterLeakHistory.slice(0, displayedCount).map((entry, index) => ({
      'Sıra': index + 1,
      'Tarih Saat': new Date(entry.timestamp).toLocaleString('tr-TR'),
      'Batarya (%)': (() => {
        if (typeof entry.battery === 'number') {
          return entry.battery.toFixed(1);
        } else if (typeof entry.battery === 'object' && entry.battery?.battery_level) {
          return entry.battery.battery_level.toFixed(1);
        }
        return 'N/A';
      })(),
      'Su Sızıntısı': entry.water_leak ? 'VAR!' : 'YOK',
      'RSSI (dBm)': entry.rssi || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { width: 8 },   // Sıra
      { width: 20 },  // Tarih Saat
      { width: 12 },  // Batarya
      { width: 15 },  // Su Sızıntısı
      { width: 12 }   // RSSI
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MSL01 Veri Geçmişi');

    const now = new Date();
    const fileName = `MSL01_${sensorMAC}_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [waterLeakHistory, displayedCount, sensorMAC]);

  // MSP01 Excel export
  const exportMSP01ToExcel = useCallback(() => {
    if (!msp01History.length) return;

    const filteredData = msp01History.filter(entry => {
      if (pirFilter === 'all') return true;
      if (pirFilter === 'active') return entry.pir_activity;
      if (pirFilter === 'inactive') return !entry.pir_activity;
      return true;
    });

    const excelData = filteredData.slice(0, displayedCount).map((entry, index) => ({
      'Sıra': index + 1,
      'Tarih Saat': new Date(entry.timestamp).toLocaleString('tr-TR'),
      'PIR Aktivitesi': entry.pir_activity ? 'VAR!' : 'YOK',
      'Sıcaklık (°C)': entry.temperature ? entry.temperature.toFixed(1) : 'N/A',
      'Işık Seviyesi (lux)': entry.illuminance ? entry.illuminance.toFixed(0) : 'N/A',
      'Batarya (%)': (() => {
        if (typeof entry.battery === 'number') {
          return entry.battery.toFixed(1);
        } else if (typeof entry.battery === 'object' && entry.battery?.battery_level) {
          return entry.battery.battery_level.toFixed(1);
        }
        return 'N/A';
      })(),
      'RSSI (dBm)': entry.rssi || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { width: 8 },   // Sıra
      { width: 20 },  // Tarih Saat
      { width: 15 },  // PIR Aktivitesi
      { width: 15 },  // Sıcaklık
      { width: 18 },  // Işık Seviyesi
      { width: 12 },  // Batarya
      { width: 12 }   // RSSI
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MSP01 Veri Geçmişi');

    const now = new Date();
    const fileName = `MSP01_${sensorMAC}_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [msp01History, displayedCount, sensorMAC]);

  // MST01 Excel export
  const exportMST01ToExcel = useCallback(() => {
    if (!mst01History.length) return;

    const baseData = [...mst01History].sort((a, b) => b.timestamp - a.timestamp);
    const dataToExport = mst01ExportScope === 'all' ? baseData : baseData.slice(0, displayedCount);

    const excelData = dataToExport.map((entry, index) => ({
      'Sıra': index + 1,
      'Tarih Saat': new Date(entry.timestamp).toLocaleString('tr-TR'),
      'Sıcaklık (°C)': entry.temperature !== null && entry.temperature !== undefined ? entry.temperature.toFixed(1) : 'N/A',
      'Nem (%)': entry.humidity !== null && entry.humidity !== undefined ? entry.humidity.toFixed(1) : 'N/A',
      'Batarya (%)': (() => {
        if (typeof entry.battery === 'number') {
          return entry.battery.toFixed(1);
        } else if (typeof entry.battery === 'object' && entry.battery?.battery_level) {
          return entry.battery.battery_level.toFixed(1);
        }
        return 'N/A';
      })(),
      'RSSI (dBm)': entry.rssi || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { width: 8 },   // Sıra
      { width: 20 },  // Tarih Saat
      { width: 15 },  // Sıcaklık
      { width: 12 },  // Nem
      { width: 12 },  // Batarya
      { width: 12 }   // RSSI
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MST01 Veri Geçmişi');

    const now = new Date();
    const scopeTag = mst01ExportScope === 'all' ? 'ALL' : `TOP_${displayedCount}`;
    const fileName = `MST01_${sensorMAC}_${scopeTag}_${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,'0')}-${now.getDate().toString().padStart(2,'0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [mst01History, displayedCount, sensorMAC, mst01ExportScope]);

  // MSL01 için özel sensör detay sayfası
  const renderMSL01Detail = () => (
    <div className="space-y-6 flex-1 overflow-y-auto">
      {/* Sensör Durumu Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Battery className="h-5 w-5 text-green-600" />
          <div>
              <p className="text-sm text-gray-600">Batarya</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history.waterleak) {
                    const waterleakEntries = Object.entries(sensor.history.waterleak);
                    if (waterleakEntries.length > 0) {
                      const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      const batteryLevel = latestData.battery || latestData.battery_level;
                      return batteryLevel ? `${batteryLevel}%` : 'N/A';
                    }
                  }
                  if (typeof sensor.battery === 'number') {
                    return `${sensor.battery}%`;
                  } else if (typeof sensor.battery === 'object' && sensor.battery !== null) {
                    if (typeof sensor.battery.battery_level === 'number') {
                      return `${sensor.battery.battery_level}%`;
                    } else if (typeof sensor.battery.level === 'number') {
                      return `${sensor.battery.level}%`;
                    }
                  }
                  return 'N/A';
                })()}
              </p>
          </div>
        </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Signal className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Sinyal</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history.waterleak) {
                    const waterleakEntries = Object.entries(sensor.history.waterleak);
                    if (waterleakEntries.length > 0) {
                      const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      return latestData.rssi ? `${latestData.rssi} dBm` : 'N/A';
                    }
                  }
                  return sensor.rssi ? `${sensor.rssi} dBm` : 'N/A';
                })()}
              </p>
      </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Son Görülme</p>
              <p className="text-sm font-semibold">
                {sensor.last_seen ? new Date(sensor.last_seen).toLocaleTimeString('tr-TR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Droplets className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Su Kaçağı</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history.waterleak) {
                    const waterleakEntries = Object.entries(sensor.history.waterleak);
                    if (waterleakEntries.length > 0) {
                      const sortedEntries = waterleakEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      return latestData.water_leak ? (
                        <span className="text-red-600">VAR!</span>
                      ) : (
                        <span className="text-green-600">YOK</span>
                      );
                    }
                  }
                  return <span className="text-gray-600">N/A</span>;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MSL01 Su Kaçağı Geçmişi Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            💧 Su Kaçağı Geçmişi
              </h3>
        </div>
        
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Su kaçağı geçmişi yükleniyor...</span>
          </div>
        ) : waterLeakChartData ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={waterLeakChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="time" 
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      domain={[0, 1]}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => value === 1 ? 'VAR!' : 'YOK'}
                    />
                    <Tooltip 
                      formatter={(value) => [value === 1 ? 'VAR!' : 'YOK', 'Su Kaçağı']}
                      labelFormatter={(label) => `Saat: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="waterLeak" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      fill="#ef4444"
                      fillOpacity={0.1}
                      dot={false}
                      activeDot={{ r: 4, fill: '#ef4444' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-gray-500">
            Su kaçağı geçmişi bulunamadı
            </div>
          )}
      </div>

      {/* MSL01 Sensör Veri Geçmişi Tablosu */}
            <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Sensör Veri Geçmişi</h2>
          <button
            onClick={exportToExcel}
            disabled={!waterLeakHistory.length}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Download className="h-3 w-3 mr-1" />
            Excel ({displayedCount})
          </button>
              </div>

        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Sensör veri geçmişi yükleniyor...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih Saat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batarya (%)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Su Sızıntısı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      RSSI (dBm)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {waterLeakHistory
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, displayedCount)
                    .map((entry, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(entry.timestamp).toLocaleString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`font-bold ${(() => {
                            const batteryValue = typeof entry.battery === 'number' 
                              ? entry.battery 
                              : typeof entry.battery === 'object' && entry.battery?.battery_level 
                                ? entry.battery.battery_level 
                                : 0;
                            return batteryValue > 50 ? 'text-green-600' : batteryValue > 20 ? 'text-yellow-600' : 'text-red-600';
                          })()}`}>
                            {(() => {
                              if (typeof entry.battery === 'number') {
                                return `${entry.battery.toFixed(1)}%`;
                              } else if (typeof entry.battery === 'object' && entry.battery?.battery_level) {
                                return `${entry.battery.battery_level.toFixed(1)}%`;
                              }
                              return 'N/A';
                            })()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`font-bold ${entry.water_leak ? 'text-red-600' : 'text-green-600'}`}>
                            {entry.water_leak ? 'VAR!' : 'YOK'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.rssi || 'N/A'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              
              {waterLeakHistory.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Sensör veri geçmişi bulunamadı
            </div>
          )}
            </div>

            {/* Load More Button */}
            {displayedCount < waterLeakHistory.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setDisplayedCount(prev => prev + 50)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  50 Kayıt Daha Göster ({waterLeakHistory.length - displayedCount} kaldı)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // MSP01 için özel sensör detay sayfası
  const renderMSP01Detail = () => (
    <div className="space-y-6 flex-1 overflow-y-auto">
      {/* Sensör Durumu Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Battery className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Batarya</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history['PIR-motion']) {
                    const pirEntries = Object.entries(sensor.history['PIR-motion']);
                    if (pirEntries.length > 0) {
                      const sortedEntries = pirEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      const batteryLevel = latestData.battery || latestData.battery_level;
                      return batteryLevel ? `${batteryLevel}%` : 'N/A';
                    }
                  }
                  if (typeof sensor.battery === 'number') {
                    return `${sensor.battery}%`;
                  } else if (typeof sensor.battery === 'object' && sensor.battery !== null) {
                    if (typeof sensor.battery.battery_level === 'number') {
                      return `${sensor.battery.battery_level}%`;
                    } else if (typeof sensor.battery.level === 'number') {
                      return `${sensor.battery.level}%`;
                    }
                  }
                  return 'N/A';
                })()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Signal className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Sinyal</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history['PIR-motion']) {
                    const pirEntries = Object.entries(sensor.history['PIR-motion']);
                    if (pirEntries.length > 0) {
                      const sortedEntries = pirEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      return latestData.rssi ? `${latestData.rssi} dBm` : 'N/A';
                    }
                  }
                  return sensor.rssi ? `${sensor.rssi} dBm` : 'N/A';
                })()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Son Görülme</p>
              <p className="text-sm font-semibold">
                {sensor.last_seen ? new Date(sensor.last_seen).toLocaleTimeString('tr-TR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
                 <div className="bg-gray-50 rounded-lg p-4">
           <div className="flex items-center space-x-2">
             <CheckCircle className="h-5 w-5 text-blue-600" />
             <div>
               <p className="text-sm text-gray-600">PIR Aktivitesi</p>
               <p className="text-lg font-semibold">
                  {msp01PirShownActive === null ? (
                    <span className="text-gray-600">N/A</span>
                  ) : msp01PirShownActive ? (
                         <span className="text-blue-600">VAR!</span>
                       ) : (
                         <span className="text-gray-600">YOK</span>
                  )}
               </p>
             </div>
           </div>
         </div>
      </div>

      {/* MSP01 Chart Grid */}
      {optimizedChartData && optimizedChartData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* PIR Aktivitesi Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">PIR Aktivitesi</h4>
              <button
                onClick={() => setFullscreenChart('pir')}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Tam ekran görünüm"
              >
                <TrendingUp className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={optimizedChartData}>
                <defs>
                  <linearGradient id="pirAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                  <YAxis 
                    domain={[0, 1]}
                    tickFormatter={(value) => value === 1 ? 'VAR!' : 'YOK'}
                  />
                  <Tooltip 
                    formatter={(value) => [value === 1 ? 'VAR!' : 'YOK', 'PIR Aktivitesi']}
                  />
                                                                <Area 
                  type="stepAfter" 
                    dataKey="pir" 
                  stroke="#1e40af" 
                    strokeWidth={2}
                  fill="url(#pirAreaGradient)"
                    dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  />
              </AreaChart>
              </ResponsiveContainer>
          </div>

          {/* Sıcaklık Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">Sıcaklık</h4>
              <button
                onClick={() => setFullscreenChart('temperature')}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Tam ekran görünüm"
              >
                <TrendingUp className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={optimizedChartData}>
                <defs>
                  <linearGradient id="tempAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                  <Tooltip 
                  formatter={(value) => [`${parseFloat(value).toFixed(2)} °C`, 'Sıcaklık']}
                  />
                                                                <Area 
                    type="monotone" 
                    dataKey="temperature" 
                  stroke="#dc2626" 
                    strokeWidth={2}
                  fill="url(#tempAreaGradient)"
                    dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  />
              </AreaChart>
              </ResponsiveContainer>
          </div>

          {/* Işık Seviyesi Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">Işık Seviyesi</h4>
              <button
                onClick={() => setFullscreenChart('illuminance')}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Tam ekran görünüm"
              >
                <TrendingUp className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={optimizedChartData}>
                <defs>
                  <linearGradient id="lightAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                  <Tooltip 
                  formatter={(value) => [`${parseFloat(value).toFixed(2)} lux`, 'Işık Seviyesi']}
                  />
                                                                <Area 
                    type="monotone" 
                    dataKey="illuminance" 
                  stroke="#d97706" 
                    strokeWidth={2}
                  fill="url(#lightAreaGradient)"
                    dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  />
              </AreaChart>
              </ResponsiveContainer>
          </div>

          {/* İvme Chart */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900">İvme</h4>
              <button
                onClick={() => setFullscreenChart('acceleration')}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Tam ekran görünüm"
              >
                <TrendingUp className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={optimizedChartData}>
                <defs>
                  <linearGradient id="accelAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                  <Tooltip 
                  formatter={(value) => [`${parseFloat(value).toFixed(2)} m/s²`, 'İvme']}
                  />
                                                                <Area 
                    type="monotone" 
                    dataKey="acceleration" 
                  stroke="#7c3aed" 
                    strokeWidth={2}
                  fill="url(#accelAreaGradient)"
                    dot={false}
                  connectNulls={true}
                  isAnimationActive={false}
                  />
              </AreaChart>
              </ResponsiveContainer>
            </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-center py-4 text-gray-500">
            MSP01 sensörü için bu zaman aralığında veri bulunmuyor
          </div>
        </div>
      )}

             {/* MSP01 Sensör Veri Geçmişi Tablosu */}
       <div className="bg-white rounded-lg shadow-sm p-6">
         <div className="flex items-center justify-between mb-6">
           <h2 className="text-lg font-semibold text-gray-900">Sensör Veri Geçmişi</h2>
           <div className="flex items-center space-x-4">
             {/* PIR Filtresi */}
             <div className="flex items-center space-x-2">
               <span className="text-sm text-gray-600">PIR Filtresi:</span>
               <select
                 value={pirFilter}
                 onChange={(e) => setPirFilter(e.target.value)}
                 className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
               >
                 <option value="all">Tümü</option>
                 <option value="active">Sadece VAR!</option>
                 <option value="inactive">Sadece YOK</option>
               </select>
             </div>
             <button
               onClick={exportMSP01ToExcel}
               disabled={!msp01History.length}
               className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
             >
               <Download className="h-3 w-3 mr-1" />
               Excel ({displayedCount})
             </button>
           </div>
         </div>

        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Sensör veri geçmişi yükleniyor...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih Saat
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PIR Aktivitesi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sıcaklık (°C)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Işık (lux)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Batarya (%)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      RSSI (dBm)
                    </th>
                  </tr>
                </thead>
                                 <tbody className="bg-white divide-y divide-gray-200">
                   {msp01History
                     .sort((a, b) => b.timestamp - a.timestamp)
                     .filter(entry => {
                       if (pirFilter === 'all') return true;
                       if (pirFilter === 'active') return entry.pir_activity;
                       if (pirFilter === 'inactive') return !entry.pir_activity;
                       return true;
                     })
                     .slice(0, displayedCount)
                     .map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(entry.timestamp).toLocaleString('tr-TR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`font-bold ${entry.pir_activity ? 'text-blue-600' : 'text-gray-600'}`}>
                            {entry.pir_activity ? 'VAR!' : 'YOK'}
                          </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.temperature !== null && entry.temperature !== undefined ? `${entry.temperature.toFixed(1)}°C` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.illuminance !== null && entry.illuminance !== undefined ? `${entry.illuminance.toFixed(0)} lux` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`font-bold ${(() => {
                            const batteryValue = typeof entry.battery === 'number' 
                              ? entry.battery 
                              : typeof entry.battery === 'object' && entry.battery?.battery_level 
                                ? entry.battery.battery_level 
                                : 0;
                            return batteryValue > 50 ? 'text-green-600' : batteryValue > 20 ? 'text-yellow-600' : 'text-red-600';
                          })()}`}>
                            {(() => {
                              if (typeof entry.battery === 'number') {
                                return `${entry.battery.toFixed(1)}%`;
                              } else if (typeof entry.battery === 'object' && entry.battery?.battery_level) {
                                return `${entry.battery.battery_level.toFixed(1)}%`;
                              }
                              return 'N/A';
                            })()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.rssi || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
                             {msp01History.filter(entry => {
                 if (pirFilter === 'all') return true;
                 if (pirFilter === 'active') return entry.pir_activity;
                 if (pirFilter === 'inactive') return !entry.pir_activity;
                 return true;
               }).length === 0 && (
                 <div className="text-center py-8 text-gray-500">
                   Seçilen filtrelere uygun veri bulunamadı
                 </div>
               )}
             </div>

             {/* Load More Button */}
             {(() => {
               const filteredData = msp01History.filter(entry => {
                 if (pirFilter === 'all') return true;
                 if (pirFilter === 'active') return entry.pir_activity;
                 if (pirFilter === 'inactive') return !entry.pir_activity;
                 return true;
               });
               return displayedCount < filteredData.length && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setDisplayedCount(prev => prev + 50)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ChevronDown className="h-4 w-4 mr-2" />
                    50 Kayıt Daha Göster ({filteredData.length - displayedCount} kaldı)
                  </button>
                </div>
              );
             })()}
          </>
        )}
      </div>
    </div>
  );

  // Genel sensör detay sayfası (diğer sensörler için)
  const renderGeneralDetail = () => (
    <div className="space-y-6 flex-1 overflow-y-auto">
      {/* Sensör Durumu Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Battery className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Batarya</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (typeof sensor.battery === 'number') {
                    return `${sensor.battery}%`;
                  } else if (typeof sensor.battery === 'object' && sensor.battery !== null) {
                    if (typeof sensor.battery.battery_level === 'number') {
                      return `${sensor.battery.battery_level}%`;
                    } else if (typeof sensor.battery.level === 'number') {
                      return `${sensor.battery.level}%`;
                    }
                  }
                  return 'N/A';
                })()}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Signal className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Sinyal</p>
              <p className="text-lg font-semibold">{sensor.rssi || 'N/A'} dBm</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Son Görülme</p>
              <p className="text-sm font-semibold">
                {sensor.last_seen ? new Date(sensor.last_seen).toLocaleTimeString('tr-TR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Durum</p>
              <p className="text-lg font-semibold text-green-600">Aktif</p>
            </div>
          </div>
        </div>
      </div>

      <div className="text-center py-8 text-gray-500">
        Bu sensör tipi için detay sayfası henüz geliştirilmemiştir.
      </div>
    </div>
  );

  // Effects
  useEffect(() => {
    if (isMSL01) {
      loadMSL01WaterLeakHistory();
    } else if (isMSP01) {
      loadMSP01History();
    } else if (isMST01) {
      loadMST01History();
    } else if (isS4) {
      loadS4History();
    }
  }, [isMSL01, isMSP01, isMST01, loadMSL01WaterLeakHistory, loadMSP01History, loadMST01History, selectedTimeRange]);

  useEffect(() => {
    updateMSL01Events();
    
    if (sensorModel.includes('MSL01')) {
      const interval = setInterval(updateMSL01Events, 30000);
      return () => clearInterval(interval);
    }
  }, [updateMSL01Events]);

  // Modal or Page Layout
  if (isModal) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={() => onBack()}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full h-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{sensorName} Detayı</h2>
                <p className="text-sm text-gray-600">Model: {sensor.model} | MAC: {sensorMAC}</p>
              </div>
            </div>
                         <div className="flex items-center space-x-4">
               {(isMSL01 || isMSP01 || isMST01 || isS4) && (
                 <div className="flex items-center space-x-2">
                   <span className="text-sm text-gray-600">Zaman aralığı:</span>
                   <select 
                     value={selectedTimeRange}
                     onChange={(e) => setSelectedTimeRange(e.target.value)}
                     className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                   >
                     <option value="1h">Son 1 Saat</option>
                     <option value="6h">Son 6 Saat</option>
                     <option value="24h">Son 24 Saat</option>
                     <option value="7d">Son 7 Gün</option>
                     <option value="30d">Son 30 Gün</option>
                   </select>
                 </div>
               )}
              <button
                onClick={() => onBack()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 flex-1 overflow-y-auto">
            {isMSL01 ? renderMSL01Detail() : 
             isMSP01 ? renderMSP01Detail() : 
             isMST01 ? renderMST01Detail() : 
             isS4 ? renderS4Detail() :
             renderGeneralDetail()}
          </div>
        </div>
      </div>
    );
  }

  // Fullscreen Chart Modal
  if (fullscreenChart && optimizedChartData && optimizedChartData.length > 0) {
    const chartTitles = {
      pir: '👁️ PIR Aktivitesi',
      temperature: '🌡️ Sıcaklık',
      illuminance: '☀️ Işık Seviyesi',
      acceleration: '📱 İvme'
    };

    const chartConfigs = {
      pir: {
        dataKey: 'pir',
        stroke: '#3b82f6',
        formatter: (value) => [value === 1 ? '🔴 Hareket Algılandı!' : '⚫ Hareket Yok', 'PIR Durumu'],
        domain: [0, 1],
        tickFormatter: (value) => value === 1 ? '🔴 VAR' : '⚫ YOK'
      },
      temperature: {
        dataKey: 'temperature',
        stroke: '#ef4444',
        formatter: (value) => value !== null ? [`🌡️ ${value.toFixed(1)}°C`, 'Sıcaklık'] : ['Veri yok', 'Sıcaklık'],
        domain: ['dataMin - 2', 'dataMax + 2'],
        tickFormatter: (value) => `${value}°C`
      },
      illuminance: {
        dataKey: 'illuminance',
        stroke: '#f59e0b',
        formatter: (value) => value !== null ? [`☀️ ${value.toFixed(0)} lux`, 'Işık Seviyesi'] : ['Veri yok', 'Işık Seviyesi'],
        domain: ['dataMin - 100', 'dataMax + 100'],
        tickFormatter: (value) => `${value} lux`
      },
      acceleration: {
        dataKey: 'acceleration',
        stroke: '#8b5cf6',
        formatter: (value) => value !== null ? [`⚡ ${value.toFixed(2)}g`, 'İvme'] : ['Veri yok', 'İvme'],
        domain: ['dataMin - 0.1', 'dataMax + 0.1'],
        tickFormatter: (value) => `${value}g`
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full h-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col border border-gray-200">
          {/* Fullscreen Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex items-center space-x-4">
              <div className={`p-3 rounded-xl ${
                fullscreenChart === 'pir' ? 'bg-blue-100' :
                fullscreenChart === 'temperature' ? 'bg-red-100' :
                fullscreenChart === 'illuminance' ? 'bg-yellow-100' :
                'bg-purple-100'
              }`}>
                <TrendingUp className={`h-6 w-6 ${
                  fullscreenChart === 'pir' ? 'text-blue-600' :
                  fullscreenChart === 'temperature' ? 'text-red-600' :
                  fullscreenChart === 'illuminance' ? 'text-yellow-600' :
                  'text-purple-600'
                }`} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{chartTitles[fullscreenChart]}</h2>
                <p className="text-sm text-gray-600 font-medium">{sensorName} - Tam Ekran Görünüm</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                ESC ile çık
            </div>
            <button
              onClick={() => setFullscreenChart(null)}
                className="p-3 hover:bg-gray-200 rounded-xl transition-colors duration-200 group"
                title="Kapat"
            >
                <X className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
            </button>
            </div>
          </div>

          {/* Fullscreen Chart */}
          <div className="p-6 flex-1 overflow-hidden">
            <div className="h-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={optimizedChartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <defs>
                    <linearGradient id={`${fullscreenChart}Gradient`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartConfigs[fullscreenChart].stroke} stopOpacity={0.9}/>
                      <stop offset="95%" stopColor={chartConfigs[fullscreenChart].stroke} stopOpacity={0.3}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    interval="preserveStartEnd"
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    domain={chartConfigs[fullscreenChart].domain}
                    tickFormatter={chartConfigs[fullscreenChart].tickFormatter}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip 
                    formatter={chartConfigs[fullscreenChart].formatter}
                    labelFormatter={(label) => `⏰ Saat: ${label}`}
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      padding: '16px'
                    }}
                    labelStyle={{ fontWeight: 'bold', color: '#374151', fontSize: '14px' }}
                  />
                  <Area 
                    type={fullscreenChart === 'pir' ? 'stepAfter' : 'monotone'}
                    dataKey={chartConfigs[fullscreenChart].dataKey} 
                    stroke={chartConfigs[fullscreenChart].stroke} 
                    strokeWidth={2}
                    fill={`url(#${fullscreenChart}Gradient)`}
                    dot={false}
                    activeDot={{ 
                      r: 8, 
                      fill: chartConfigs[fullscreenChart].stroke, 
                      stroke: '#ffffff', 
                      strokeWidth: 3,
                      filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
                    }}
                    connectNulls={true}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render fonksiyonları
  function renderMST01Detail() {
    return (
    <div className="space-y-6 flex-1 overflow-y-auto">
      {/* Üst widgetlar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Batarya */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Battery className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-gray-600">Batarya</p>
              <p className="text-lg font-semibold">
                {(() => {
                  // En güncel batarya
                  if (sensor.history && sensor.history.temperature) {
                    const tempEntries = Object.entries(sensor.history.temperature);
                    if (tempEntries.length > 0) {
                      const sortedEntries = tempEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      const batteryLevel = latestData.battery || latestData.battery_level;
                      return batteryLevel ? `${batteryLevel}%` : 'N/A';
                    }
                  }
                  if (typeof sensor.battery === 'number') return `${sensor.battery}%`;
                  if (typeof sensor.battery === 'object' && sensor.battery?.battery_level) return `${sensor.battery.battery_level}%`;
                  return 'N/A';
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* RSSI */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Signal className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">RSSI</p>
              <p className="text-lg font-semibold">
                {(() => {
                  if (sensor.history && sensor.history.temperature) {
                    const tempEntries = Object.entries(sensor.history.temperature);
                    if (tempEntries.length > 0) {
                      const sortedEntries = tempEntries.sort(([a], [b]) => parseInt(b) - parseInt(a));
                      const latestData = sortedEntries[0][1];
                      return latestData.rssi ? `${latestData.rssi} dBm` : 'N/A';
                    }
                  }
                  return sensor.rssi ? `${sensor.rssi} dBm` : 'N/A';
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Son Görülme */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Son Görülme</p>
              <p className="text-lg font-semibold">{sensor.last_seen ? new Date(sensor.last_seen).toLocaleString('tr-TR') : 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Sıcaklık & Nem (son değer) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Thermometer className="h-5 w-5 text-red-600" />
            <Droplets className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-gray-600">Sıcaklık & Nem (son)</p>
              <p className="text-lg font-semibold">
                {(() => {
                  const latest = [...(mst01History || [])].sort((a,b)=>b.timestamp-a.timestamp)[0];
                  if (!latest) return 'N/A / N/A';
                  const tempTxt = latest.temperature !== null && latest.temperature !== undefined ? `${latest.temperature.toFixed(1)}°C` : 'N/A';
                  const humTxt = latest.humidity !== null && latest.humidity !== undefined ? `${latest.humidity.toFixed(1)}%` : 'N/A';
                  return `${tempTxt} / ${humTxt}`;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Geniş birleşik chart: Sıcaklık & Nem */}
      {mst01ChartData && mst01ChartData.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Sıcaklık & Nem Geçmişi</h4>
            <button
              onClick={() => setFullscreenChart('tempHumidity')}
              className="p-2 hover:bg-gray-100 rounded transition-colors"
              title="Tam ekran görünüm"
            >
              <TrendingUp className="h-5 w-5 text-gray-600" />
            </button>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={mst01ChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="temp" orientation="left" label={{ value: 'Sıcaklık (°C)', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="hum" orientation="right" label={{ value: 'Nem (%)', angle: 90, position: 'insideRight' }} />
              <Tooltip 
                formatter={(value, name) => [
                  name === 'temperature' ? `${Number(value).toFixed(1)}°C` : `${Number(value).toFixed(1)}%`,
                  name === 'temperature' ? 'Sıcaklık' : 'Nem'
                ]}
              />
              <Line 
                yAxisId="temp"
                type="monotone" 
                dataKey="temperature" 
                stroke="#dc2626" 
                strokeWidth={2}
                dot={false}
                connectNulls={true}
                name="temperature"
              />
              <Line 
                yAxisId="hum"
                type="monotone" 
                dataKey="humidity" 
                stroke="#2563eb" 
                strokeWidth={2}
                dot={false}
                connectNulls={true}
                name="humidity"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <p className="text-center text-gray-500">Sıcaklık & nem verisi bulunamadı</p>
          </div>
      )}

      {/* Sensör Veri Geçmişi Tablosu */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">Sensör Veri Geçmişi</h4>
          <div className="flex items-center space-x-3">
            <select
              value={mst01ExportScope}
              onChange={(e) => setMst01ExportScope(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="visible">Görünen Kayıtlar</option>
              <option value="all">Tüm Kayıtlar</option>
            </select>
            <button
              onClick={exportMST01ToExcel}
              disabled={!mst01History.length}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Download className="h-3 w-3 mr-1" />
              Excel {mst01ExportScope === 'all' ? '(Tümü)' : `(${displayedCount})`}
            </button>
          </div>
        </div>

        {isLoadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Sensör veri geçmişi yükleniyor...</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih Saat</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sıcaklık (°C)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nem (%)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batarya (%)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RSSI (dBm)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {mst01History
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, displayedCount)
                    .map((entry, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(entry.timestamp).toLocaleString('tr-TR')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.temperature !== null && entry.temperature !== undefined ? `${entry.temperature.toFixed(1)}°C` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.humidity !== null && entry.humidity !== undefined ? `${entry.humidity.toFixed(1)}%` : 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <span className={`font-bold ${(() => {
                            const batteryValue = typeof entry.battery === 'number' 
                              ? entry.battery 
                              : typeof entry.battery === 'object' && entry.battery?.battery_level 
                                ? entry.battery.battery_level 
                                : 0;
                            return batteryValue > 50 ? 'text-green-600' : batteryValue > 20 ? 'text-yellow-600' : 'text-red-600';
                          })()}`}>
                            {(() => {
                              if (typeof entry.battery === 'number') return `${entry.battery.toFixed(1)}%`;
                              if (typeof entry.battery === 'object' && entry.battery?.battery_level) return `${entry.battery.battery_level.toFixed(1)}%`;
                              return 'N/A';
                            })()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.rssi || 'N/A'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {mst01History.length === 0 && (
                <div className="text-center py-8 text-gray-500">Sensör veri geçmişi bulunamadı</div>
              )}
            </div>
            {/* Load More Button */}
            {displayedCount < mst01History.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setDisplayedCount(prev => prev + 50)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ChevronDown className="h-4 w-4 mr-2" />
                  50 Kayıt Daha Göster ({mst01History.length - displayedCount} kaldı)
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    );
  }

  function renderS4Detail() {
    return (
      <div className="space-y-6 flex-1 overflow-y-auto">
        {/* Üst widgetlar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Batarya */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Battery className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Batarya</p>
                <p className="text-lg font-semibold">
                  {(() => {
                    const latest = [...(s4History || [])].sort((a,b)=>b.timestamp-a.timestamp)[0];
                    const batteryLevel = latest?.battery ?? (typeof sensor.battery === 'number' ? sensor.battery : (sensor.battery?.battery_level));
                    return typeof batteryLevel === 'number' ? `${batteryLevel}%` : 'N/A';
                  })()}
                </p>
              </div>
            </div>
          </div>

          {/* RSSI */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Signal className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">RSSI</p>
                <p className="text-lg font-semibold">
                  {(() => {
                    const latest = [...(s4History || [])].sort((a,b)=>b.timestamp-a.timestamp)[0];
                    return latest?.rssi ? `${latest.rssi} dBm` : (sensor.rssi ? `${sensor.rssi} dBm` : 'N/A');
                  })()}
                </p>
              </div>
            </div>
          </div>

          {/* Son Görülme */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">Son Görülme</p>
                <p className="text-lg font-semibold">{sensor.last_seen ? new Date(sensor.last_seen).toLocaleString('tr-TR') : 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Kapı Durumu (son) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-indigo-600" />
              <div>
                <p className="text-sm text-gray-600">Kapı Durumu (son)</p>
                <p className="text-lg font-semibold">
                  {(() => {
                    const latest = [...(s4History || [])].sort((a,b)=>b.timestamp-a.timestamp)[0];
                    if (latest && latest.door_open !== null && latest.door_open !== undefined) {
                      return latest.door_open ? 'AÇIK' : 'KAPALI';
                    }
                    // Fall back to current sensor binary/contact state if available
                    const bs = sensor.binary_states || sensor.binary_state || {};
                    const doorVal = bs.door_open ?? bs.door ?? bs.contact ?? bs.open ?? bs.closed;
                    if (doorVal === true) return 'AÇIK';
                    if (doorVal === false) return 'KAPALI';
                    if (doorVal === 'open' || doorVal === 1) return 'AÇIK';
                    if (doorVal === 'closed' || doorVal === 0) return 'KAPALI';
                    return 'N/A';
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tek grafik: Kapı & Tamper */}
        {optimizedS4ChartData && optimizedS4ChartData.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Kapı & Tamper Geçmişi</h4>
              <button
                onClick={() => setFullscreenChart('s4DoorTamper')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Tam ekran görünüm"
              >
                <TrendingUp className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={optimizedS4ChartData}>
                <defs>
                  <linearGradient id="doorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.3}/>
                  </linearGradient>
                  <linearGradient id="tamperGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.9}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis domain={[0, 1]} tickFormatter={(v) => v === 1 ? 'AÇIK/VAR' : 'KAPALI/YOK'} />
                <Tooltip formatter={(value, name) => [value === 1 ? (name === 'door' ? 'AÇIK' : 'VAR') : (name === 'door' ? 'KAPALI' : 'YOK'), name === 'door' ? 'Kapı' : 'Tamper']} />
                <Legend />
                <Area type="stepAfter" dataKey="door" name="Kapı" stroke="#059669" strokeWidth={2} fill="url(#doorGradient)" dot={false} connectNulls isAnimationActive={false} />
                <Area type="stepAfter" dataKey="tamper" name="Tamper" stroke="#d97706" strokeWidth={2} fill="url(#tamperGradient)" dot={false} connectNulls isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-center text-gray-500">Kapı/tamper verisi bulunamadı</p>
          </div>
        )}

        {/* Sensör Veri Geçmişi Tablosu */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">Sensör Veri Geçmişi</h4>
            <div className="flex items-center space-x-3">
              <select
                value={s4ExportScope}
                onChange={(e) => setS4ExportScope(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="visible">Görünen Kayıtlar</option>
                <option value="all">Tüm Kayıtlar</option>
              </select>
              <button
                onClick={exportS4ToExcel}
                disabled={!s4History.length}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <Download className="h-3 w-3 mr-1" />
                Excel {s4ExportScope === 'all' ? '(Tümü)' : `(${displayedCount})`}
              </button>
            </div>
          </div>
          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Sensör veri geçmişi yükleniyor...</span>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarih Saat</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kapı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamper</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Açılma Sayısı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kapanma Sayısı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tamper Sayısı</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batarya (%)</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RSSI (dBm)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {s4History
                      .slice()
                      .sort((a, b) => b.timestamp - a.timestamp)
                      .slice(0, displayedCount)
                      .map((e, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(e.timestamp).toLocaleString('tr-TR')}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-bold ${e.door_open ? 'text-green-600' : 'text-gray-700'}`}>{e.door_open ? 'AÇIK' : 'KAPALI'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-bold ${e.tamper ? 'text-yellow-600' : 'text-gray-700'}`}>{e.tamper ? 'VAR' : 'YOK'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{typeof e.open_count === 'number' ? e.open_count : 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{typeof e.close_count === 'number' ? e.close_count : 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{typeof e.tamper_count === 'number' ? e.tamper_count : 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={`font-bold ${(() => {
                              const batteryValue = typeof e.battery === 'number' ? e.battery : (typeof e.battery === 'object' && e.battery?.battery_level ? e.battery.battery_level : 0);
                              return batteryValue > 50 ? 'text-green-600' : batteryValue > 20 ? 'text-yellow-600' : 'text-red-600';
                            })()}`}>
                              {(() => {
                                if (typeof e.battery === 'number') return `${e.battery.toFixed(1)}%`;
                                if (typeof e.battery === 'object' && e.battery?.battery_level) return `${e.battery.battery_level.toFixed(1)}%`;
                                return 'N/A';
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{e.rssi || 'N/A'}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {s4History.length === 0 && (
                  <div className="text-center py-8 text-gray-500">Sensör veri geçmişi bulunamadı</div>
                )}
              </div>
              {displayedCount < s4History.length && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => setDisplayedCount(prev => prev + 50)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <ChevronDown className="h-4 w-4 mr-2" />
                    50 Kayıt Daha Göster ({s4History.length - displayedCount} kaldı)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Page Layout (fallback)
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{sensorName}</h1>
            <p className="text-sm text-gray-500">MAC: {sensorMAC}</p>
          </div>
        </div>
         {(isMSL01 || isMSP01 || isMST01) && (
           <div className="flex items-center space-x-4">
             <select
               value={selectedTimeRange}
               onChange={(e) => setSelectedTimeRange(e.target.value)}
               className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
             >
               <option value="1h">Son 1 Saat</option>
               <option value="6h">Son 6 Saat</option>
               <option value="24h">Son 24 Saat</option>
               <option value="7d">Son 7 Gün</option>
               <option value="30d">Son 30 Gün</option>
             </select>
           </div>
         )}
       </div>
      {isMSL01 ? renderMSL01Detail() : 
       isMSP01 ? renderMSP01Detail() : 
       isMST01 ? renderMST01Detail() : 
       isS4 ? renderS4Detail() :
        renderGeneralDetail()}
    </div>
  );
};

export default SensorDetail;
